import React from 'react';
import { useFirebase } from '../../hooks/useFirebase';
import { formatInTimeZone } from 'date-fns-tz';
import { FirebaseReading } from '../../types/firebase';
import { computeWeatherCondition } from '../../utils/weather';
import { TIME_RANGES } from '../../utils/constants';

const MIN_POINTS = 8;
const STEPS = 5;
const STEP_HOURS = 3;

const toMs = (timestamp: string | number) => {
  if (typeof timestamp === 'number') return timestamp;
  if (typeof timestamp === 'string' && /^\d+$/.test(timestamp.trim())) {
    return parseInt(timestamp.trim(), 10);
  }
  const parsed = new Date(timestamp).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const linearSlopePerHour = (points: { t: number; v: number }[]) => {
  if (points.length < 2) return 0;
  const n = points.length;
  const meanT = points.reduce((s, p) => s + p.t, 0) / n;
  const meanV = points.reduce((s, p) => s + p.v, 0) / n;
  let num = 0;
  let den = 0;
  points.forEach((p) => {
    const dt = p.t - meanT;
    num += dt * (p.v - meanV);
    den += dt * dt;
  });
  if (den === 0) return 0;
  const slopePerMs = num / den;
  return slopePerMs * 3_600_000; // per hour
};

const buildHourlyForecast = (data: FirebaseReading[]) => {
  const temps = data
    .map((d) => ({
      t: toMs(d.timestamp),
      temp: d.temperature,
      hum: d.humidity,
      pressure: d.pressure,
      light: d.light,
      rain: d.rain,
      windSpeed: d.windSpeed,
    }))
    .filter((d) => d.t !== null && d.temp !== null) as {
    t: number;
    temp: number;
    hum?: number | null;
    pressure?: number | null;
    light?: number | null;
    rain?: number | null;
    windSpeed?: number | null;
  }[];

  if (temps.length < MIN_POINTS) return [];

  const sorted = temps.sort((a, b) => a.t - b.t);
  const slopeTemp = linearSlopePerHour(sorted.map(({ t, temp }) => ({ t, v: temp })));
  const last = sorted[sorted.length - 1];
  const baseTemp = last.temp;
  const baseHum = last.hum ?? 60;
  const baseWind = last.windSpeed ?? 3;

  const out = [];
  for (let i = 1; i <= STEPS; i++) {
    const hoursAhead = i * STEP_HOURS;
    const projectedTemp = baseTemp + slopeTemp * hoursAhead;
    const projectedHum = baseHum;
    const projectedWind = baseWind;
    const condition = computeWeatherCondition(
      projectedTemp,
      projectedHum,
      last.pressure ?? null,
      last.light ?? null,
      last.rain ?? null,
      projectedWind ?? null
    );

    const ts = last.t + hoursAhead * 3_600_000;
    const time = formatInTimeZone(new Date(ts), 'Asia/Manila', 'h:mm a');

    out.push({
        time,
      temp: projectedTemp.toFixed(0),
      windSpeed: projectedWind.toFixed(0),
        condition,
      });
    }
    
  return out;
  };

  const getConditionIcon = (condition: string): string => {
    const iconMap: Record<string, string> = {
      sunny: '/Sunny.svg',
      'partly-cloudy': '/Partly Cloudy.svg',
      cloudy: '/Cloudy.svg',
      rainy: '/Rainy.svg',
    foggy: '/Foggy.svg',
    hot: '/Hot and Dry.svg',
    cold: '/Cold.svg',
    };
    return iconMap[condition] || '/Sunny.svg';
  };

export const HourlyForecastCard: React.FC = () => {
  const { historicalData, loadHistoricalData } = useFirebase();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadHistoricalData(TIME_RANGES.DAY); // past 24h is enough for short-term projection
      } catch (e) {
        if (!active) return;
        setError('Failed to load data for hourly forecast');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [loadHistoricalData]);

  const hourlyForecast = React.useMemo(
    () => buildHourlyForecast(historicalData),
    [historicalData]
  );

  const renderBody = () => {
    if (loading) {
      return <div className="text-sm text-gray-400">Loading hourly forecast...</div>;
    }
    if (error) {
      return <div className="text-sm text-red-400">{error}</div>;
    }
    if (hourlyForecast.length === 0) {
      return (
        <div className="text-sm text-gray-400">
          Not enough recent data to generate an hourly forecast yet.
        </div>
      );
    }

  return (
      <div className="space-y-3">
        {hourlyForecast.map((item, index) => (
          <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src={getConditionIcon(item.condition)} 
                  alt={item.condition} 
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
              <div>
                <div className="font-medium">{item.time}</div>
                <div className="text-sm text-gray-400 flex items-center gap-1">
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.5 17a4.5 4.5 0 01-1.44-8.765 4.5 4.5 0 018.302-3.046 3.5 3.5 0 014.504 4.272A4 4 0 0115 17H5.5zm3.75-2a.75.75 0 00-1.5 0v.5a.75.75 0 001.5 0v-.5zm2.25-4a.75.75 0 00-1.5 0v.5a.75.75 0 001.5 0v-.5zM9.75 9a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5z" clipRule="evenodd" />
                  </svg>
                  {item.windSpeed}km/h
                </div>
              </div>
            </div>
            <div className="text-xl font-semibold">{item.temp}°C</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className="p-6 rounded-3xl"
      style={{
        backgroundColor: 'rgba(30, 30, 35, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <div className="text-xl font-semibold mb-4">Hourly Forecast:</div>
      {renderBody()}
    </div>
  );
};

